import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ActionConfigModal, { ActionConfig, KeyValue } from "../../../components/common/ActionConfigModal";
import { agentInstanceService, agentService } from "../../../services/agentService";
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Surface } from '../../ui/Surface';

type Capability = {
  id: string;
  capabilityCode: string;
  updatedAt?: string;
};

/**
 * Convert Record<string, any> to KeyValue array
 */
const objectToKeyValuesArray = (obj: Record<string, any> | undefined): KeyValue[] => {
  if (!obj) return [];
  
  return Object.entries(obj).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
};

const ActionsTab: React.FC<{ agent: any }> = ({ agent }) => {
  const [capabilities, setCapabilities] = useState<any[]>([]);
  const [actions, setActions] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState<any>(null);

  // Load agent type capabilities
  useEffect(() => {
    const loadCapabilities = async () => {
      const agentTypeId = agent?.agentType?.id || agent?.agentTypeId;
      if (!agentTypeId) return;
      try {
        setLoading(true);
        setError(null);
        const agentType = await agentService.getAgentTypeById(agentTypeId);
        setCapabilities(agentType?.capabilities || []);
      } catch (err: any) {
        setError(err?.message || 'Failed to load capabilities');
      } finally {
        setLoading(false);
      }
    };
    loadCapabilities();
  }, [agent?.agentType?.id, agent?.agentTypeId]);

  // Load actions for this agent instance
  useEffect(() => {
    loadActions();
  }, [agent?.id]);

  const loadActions = async () => {
    if (!agent?.id) return;
    try {
      const result = await agentInstanceService.listAgentActions(agent.id, { limit: 100, includeDeleted: true });
      const actionMap: Record<string, any> = {};
      (result.items || []).forEach((action: any) => {
        if (action?.headers) {
          const headers: any = {...(typeof action.headers === "string" ? JSON.parse(action.headers) : action.headers)}
          const formattedHeaders: KeyValue[] = objectToKeyValuesArray(headers)
          action.headers = [...formattedHeaders]
        }

        if (action?.queryParams) {
          const queryParams: any = {...(typeof action.queryParams === "string" ? JSON.parse(action.queryParams) : action.queryParams)}
          const formattedQueryParams: KeyValue[] = objectToKeyValuesArray(queryParams)
          action.queryParams = [...formattedQueryParams]
        }

        if (action?.bodyTemplate) {
          const bodyTemplate: any = {...(typeof action.bodyTemplate === "string" ? JSON.parse(action.bodyTemplate) : action.bodyTemplate)}
          const formattedBodyTemplate: KeyValue[] = objectToKeyValuesArray(bodyTemplate)
          action.bodyTemplate = [...formattedBodyTemplate]
        }
        if (action?.mcpInputTemplate) {
          action.mcpInputTemplate =
            typeof action.mcpInputTemplate === "string"
              ? JSON.parse(action.mcpInputTemplate)
              : action.mcpInputTemplate;
        }

        actionMap[action.capabilityCode] = action;
      });
      setActions(actionMap);
    } catch (err: any) {
      console.error('Failed to load actions:', err);
    }
  };

  const handleConfigureAction = (capability: any) => {
    setSelectedCapability(capability);
    setIsActionModalOpen(true);
  };

  const handleActionSubmit = async (config: any) => {
    if (!agent?.id || !selectedCapability) return;

    try {
      const existingAction = actions[selectedCapability.capabilityCode];
      const payload = {
        capabilityCode: selectedCapability.capabilityCode,
        name: config.name,
        description: config.description,
        bindingType: config.bindingType || 'api_endpoint',
        mcpServerId: config.mcpServerId || null,
        mcpToolName: config.mcpToolName || null,
        mcpInputTemplate: (config.mcpInputTemplate || {}) as Record<string, any>,
        method: config.method,
        url: config.url || '',
        headers: Object.fromEntries(config.headers.map((h: any) => [h.key, h.value])),
        queryParams: Object.fromEntries(config.queryParams.map((p: any) => [p.key, p.value])),
        bodyTemplate: Object.fromEntries(config.bodyTemplate.map((p: any) => [p.key, p.value])),
        bodyTemplateWithMetaData: (config.bodyTemplateWithMetaData || {}) as any,
      };

      if (existingAction) {
        // Update existing
        await agentInstanceService.updateAgentAction(agent.id, existingAction.id, payload);
        toast.success('Action updated successfully');
      } else {
        // Create new
        await agentInstanceService.createAgentAction(agent.id, payload);
        toast.success('Action created successfully');
      }

      // Refresh actions list
      loadActions()

      setIsActionModalOpen(false);
      setSelectedCapability(null);
    } catch (err: any) {
      console.error('Error saving action:', err);
      toast.error(err?.message || 'Failed to save action configuration');
    }
  };

  if (loading) return <div className="p-6">Loading capabilities...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

  return (
    <div className="p-6">
      <h3 className="mb-6 text-lg font-semibold text-agentic-text-primary">
        Available Actions
      </h3>

      {capabilities.length === 0 ? (
        <div className="text-sm text-agentic-text-secondary">
          No capabilities found for this agent type
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {capabilities.map((cap: any, index: number) => {
            const capabilityCode = typeof cap === "string" ? cap : cap?.capabilityCode;
            if (!capabilityCode) return null;
            const action = actions[capabilityCode];
            return (
              <Surface tone="card-hover" key={cap?.id || capabilityCode || index} className="rounded-xl border p-5 transition-shadow">
                <div className="mb-3 flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-agentic-text-primary">{capabilityCode}</h4>
                    {action && (
                      <p className="text-sm text-agentic-text-secondary mt-1">
                        {action.method} {action.url}
                      </p>
                    )}
                  </div>
                  {action && (
                    <Badge tone="success" className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ml-2">
                      Configured
                    </Badge>
                  )}
                </div>

                <Button tone="custom"
                  onClick={() => handleConfigureAction(typeof cap === "string" ? { capabilityCode } : cap)}
                  className="mt-3 rounded-lg bg-agentic-primary-blue px-3 py-1 text-sm text-white hover:bg-agentic-primary-blue-90 transition-colors"
                >
                  {action ? 'Update' : 'Configure'}
                </Button>
              </Surface>
            );
          })}
        </div>
      )}

      <ActionConfigModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        initialConfig={
          selectedCapability ? actions[selectedCapability.capabilityCode] : undefined
        }
        capabilityCode={selectedCapability?.capabilityCode}
        onSubmit={handleActionSubmit}
        capabilityDetails={selectedCapability}
        agentInstanceId={agent?.id}
        availableSourceActions={Object.values(actions)
          .filter(
            (a: any) =>
              a?.capabilityCode &&
              a.capabilityCode !== selectedCapability?.capabilityCode
          )
          .map((a: any) => ({
            capabilityCode: a.capabilityCode,
            name: a.name || a.capabilityCode,
          }))}
      />
    </div>
  );
};

export default ActionsTab;
